import { CommonModule } from '@angular/common';
import { Component, inject, signal, ViewChild } from '@angular/core';
import { ButtonModule } from 'primeng/button';
import { StepperModule } from 'primeng/stepper';
import { ToastModule } from 'primeng/toast';
import { TooltipModule } from 'primeng/tooltip';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators, FormArray } from '@angular/forms';
import { InputTextModule } from 'primeng/inputtext';
import { MessageModule } from 'primeng/message';
import { CheckboxModule } from 'primeng/checkbox';
import { SelectModule } from 'primeng/select';
import { Router } from '@angular/router';
import { Routes } from 'src/app/core/routes/routes';
import { OfficeApi } from '@features/business/services/office-api';
import { BusinessApi } from '@features/business/services/business-api';
import { DataEndpoint } from '@core/models/general.model';
import { OfficeCreateRequestModel } from '@features/business/models/office.model';
import { BusinessGroupedModel } from '@features/business/models/business.model';
import { OfficeBulkImportRow, OfficeBulkImportResult, ExcelColumnMap, OfficeBulkImportError } from '@features/business/models/office-bulk-import.model';
import { HttpErrorResponse } from '@angular/common/http';
import { MessageService } from 'primeng/api';
import { FormFooter } from "@shared/components/form-footer/form-footer";
import { AccordionModule } from 'primeng/accordion';
import { CountryService } from '@shared/services/country-service/country-service';
import { Country } from '@shared/models/location.model';
import { BusinessLogo } from "@shared/components/business-logo/business-logo";
import { FlagImage } from "@shared/components/flag-image/flag-image";
import { AppDialog } from "@shared/components/dialog";
import { BlockUI } from "primeng/blockui";
import { ProgressSpinner } from "primeng/progressspinner";
import { FileUpload } from 'primeng/fileupload';
import * as XLSX from 'xlsx';

interface OfficeFormData {
  name: string;
  address: string;
  country: string;
  state: string;
  isMain: boolean;
}

@Component({
  selector: 'app-office-bulk-form',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    InputTextModule,
    CheckboxModule,
    ButtonModule,
    MessageModule,
    StepperModule,
    ToastModule,
    SelectModule,
    FormFooter,
    AccordionModule,
    TooltipModule,
    BusinessLogo,
    FlagImage,
    AppDialog,
    BlockUI,
    FileUpload,
    ProgressSpinner
  ],
  templateUrl: './office-bulk-form.html',
  styleUrl: './office-bulk-form.scss',
  providers: [MessageService]
})

export class OfficeBulkForm {
  readonly router = inject(Router);
  readonly messageService = inject(MessageService);
  readonly countryService = inject(CountryService);
  readonly officeApi = inject(OfficeApi);
  readonly businessApi = inject(BusinessApi);

  @ViewChild('fileUpload') fileUpload: FileUpload | undefined;

  uploadedFiles: any[] = [];
  fileUrl: string = '';
  fileName: string = '';

  businessGroupOptions: BusinessGroupedModel[] = [];

  countryOptions: Country[] = [];

  form: FormGroup = new FormGroup({
    businessId: new FormControl('', [Validators.required]),
    offices: new FormArray([])
  });

  isLoading = false;
  activeStep: number = 1;
  activeAccordion: number | null = null;

  visibleSuccessDialog: boolean = false;
  dialogTitle: string = '';
  dialogMessage: string = '';
  fileProcessedSuccessfully: boolean = false;

  ngOnInit() {
    this.getBusinessGroupList();
    this.getCountryList();
    this.addOfficeForm(); // Agregar primera sede por defecto
  }

  activeAccordionChange(index : number){
    this.activeAccordion = this.activeAccordion === index ? null : index;
  }

  onSelectFile(event: any) {
    for (let file of event.files) {
      this.uploadedFiles.push(file);
      this.form.patchValue({'excel-file': file});
      this.fileName = file.name;
      this.fileUrl = file.name;
      this.fileProcessedSuccessfully = false;

      // Leer el archivo Excel
      this.readExcelFile(file);
    }
  }

  removeFile() {
    this.uploadedFiles = [];
    this.form.get('excel-file')?.setValue('');
    this.fileUrl = '';
    this.fileName = '';

    if (this.fileUpload) {
      this.fileUpload.clear();
    }
    this.fileProcessedSuccessfully = false;
  }

  private readExcelFile(file: File) {
    const reader = new FileReader();

    reader.onload = (event: any) => {
      try {
        const binaryString = event.target.result;
        const workbook = XLSX.read(binaryString, { type: 'binary' });

        // Leer específicamente de la hoja "Main"
        const sheetName = 'Main';
        if (!workbook.SheetNames.includes(sheetName)) {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'No se encontró la hoja "Main" en el archivo Excel.'
          });
          return;
        }

        const worksheet = workbook.Sheets[sheetName];

        // Convertir hoja a JSON con toda la información
        const rawData = XLSX.utils.sheet_to_json(worksheet, { header: 1 }) as any[];

        // Procesar los datos del Excel
        this.processExcelData(rawData);
      } catch (error) {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Error al procesar el archivo Excel. Verifique el formato.'
        });
      }
    };

    reader.readAsBinaryString(file);
  }

  private processExcelData(data: any[]) {
    if (!data || data.length < 3) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Advertencia',
        detail: 'El archivo Excel no tiene la estructura esperada. Verifique que tenga encabezados en la fila 2 y datos a partir de la fila 3.'
      });
      return;
    }

    // La fila 1 (índice 0) contiene v1.0, ignoramos
    // La fila 2 (índice 1) contiene los encabezados en columnas B-G
    const headers = data[1] as string[];

    // Verificar que hay encabezados
    if (!headers || headers.length === 0) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'No se encontraron encabezados en la fila 2 del Excel.'
      });
      return;
    }

    // Encontrar índices de columnas por nombre
    const columnMap = this.getColumnMap(headers);

    // Validar que se encontraron todas las columnas necesarias
    if (!this.validateColumnMap(columnMap)) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'No se pudieron identificar todas las columnas necesarias en el Excel. Verifique que tenga las columnas: Nombre de la sede, Dirección, País, Región y Principal.'
      });
      return;
    }

    // Procesar y validar datos (comenzar desde la fila 3, índice 2)
    const importResult = this.processAndValidateRows(data, columnMap, 2);

    // Mostrar resumen
    this.showImportSummary(importResult);

    // Si hay datos válidos, cargar en el formulario
    if (importResult.offices.length > 0) {
      this.populateOfficesFromImport(importResult.offices);
    }
  }

  private processAndValidateRows(data: any[], columnMap: ExcelColumnMap, startIndex: number): OfficeBulkImportResult {
    const result: OfficeBulkImportResult = {
      totalRows: 0,
      successCount: 0,
      errorCount: 0,
      offices: [],
      errors: []
    };

    // Procesar cada fila de datos (comenzar desde startIndex, omitiendo encabezados)
    for (let i = startIndex; i < data.length; i++) {
      const row = data[i];

      // Saltar filas completamente vacías
      if (!row || row.length === 0) {
        continue;
      }

      result.totalRows++;

      try {
        const officeData = this.extractOfficeDataFromRow(row, columnMap, i + 1);

        if (officeData) {
          result.offices.push(officeData);
          result.successCount++;
        }
      } catch (error: any) {
        result.errorCount++;
        result.errors.push({
          rowNumber: i + 1,
          message: error.message,
          field: error.field
        });
      }
    }

    return result;
  }

  private extractOfficeDataFromRow(row: any[], columnMap: ExcelColumnMap, rowNumber: number): OfficeBulkImportRow | null {
    // Extraer datos de la fila
    const nombre = (row[columnMap.nombre] || '').toString().trim();
    const direccion = (row[columnMap.direccion] || '').toString().trim();
    const pais = (row[columnMap.pais] || '').toString().trim();
    const region = (row[columnMap.region] || '').toString().trim();
    const principalRaw = row[columnMap.principal];
    const principal = this.parseBoolean(principalRaw);

    // Validar campos requeridos
    if (!nombre) {
      throw {
        message: 'El nombre de la sede es requerido',
        field: 'nombre'
      };
    }

    if (!direccion) {
      throw {
        message: 'La dirección es requerida',
        field: 'direccion'
      };
    }

    if (!pais) {
      throw {
        message: 'El país es requerido',
        field: 'pais'
      };
    }

    // Validar que el código de país sea válido
    const country = this.findCountryByCode(pais);
    if (!country) {
      throw {
        message: `El código de país "${pais}" no es válido`,
        field: 'pais'
      };
    }

    return {
      nombre,
      direccion,
      // Usar iso2 como código de país importado
      pais: country.iso2,
      region,
      principal
    };
  }

  private showImportSummary(result: OfficeBulkImportResult) {
    if (result.successCount > 0) {
      if (result.errorCount > 0) {
        const errorDetails = result.errors
          .map(e => `Fila ${e.rowNumber}: ${e.message}`)
          .join('\n');

        const unprocessedCountries = this.getUnprocessedCountries(result.errors);

        this.messageService.add({
          severity: 'error',
          summary: 'Error en la importación',
          detail: `El archivo contiene países que no se procesaron: ${unprocessedCountries}. Corrige el archivo y vuelve a intentarlo;`,
          life: 5000
        });

        console.warn('Errores de importación:', errorDetails);
        this.fileProcessedSuccessfully = false;
      } else {
        const detail = `Se procesaron correctamente ${result.successCount} sede(s).`;

        this.messageService.add({
          severity: 'success',
          summary: 'Importación exitosa',
          detail: detail,
          life: 4000
        });

        this.fileProcessedSuccessfully = true;
      }
    } else if (result.errorCount > 0) {
      const errorDetails = result.errors
        .slice(0, 5)
        .map(e => `Fila ${e.rowNumber}: ${e.message}`)
        .join('\n');

      const unprocessedCountries = this.getUnprocessedCountries(result.errors);

      this.messageService.add({
        severity: 'error',
        summary: 'Error en la importación',
        detail: `El archivo contiene países que no se procesaron: ${unprocessedCountries}. Corrige el archivo y vuelve a intentarlo.`,
        life: 5000
      });
      console.warn('Errores de importación:', errorDetails);
      this.fileProcessedSuccessfully = false;
    }
  }

  private populateOfficesFromImport(offices: OfficeBulkImportRow[]) {
    const officesArray = this.form.get('offices') as FormArray;
    officesArray.clear();
    offices.forEach(officeData => {
      const officeForm = this.createOfficeFormGroup();

      officeForm.patchValue({
        name: officeData.nombre,
        address: officeData.direccion,
        country: officeData.pais,
        state: officeData.region,
        isMain: officeData.principal
      });

      officesArray.push(officeForm);
    });
  }

  private validateColumnMap(columnMap: ExcelColumnMap): boolean {
    return columnMap.nombre >= 0 &&
           columnMap.direccion >= 0 &&
           columnMap.pais >= 0;
  }

  private getColumnMap(headers: string[]): any {
    // Mapear nombres de columnas
    const headerMap: { [key: string]: number } = {};

    headers.forEach((header, index) => {
      const lowerHeader = header.toLowerCase().trim();
      headerMap[lowerHeader] = index;
    });

    const columnMap = {
      nombre: this.findColumnIndex(headerMap, ['nombre de la sede', 'nombre']),
      direccion: this.findColumnIndex(headerMap, ['dirección', 'direccion', 'address']),
      pais: this.findColumnIndex(headerMap, ['código de país', 'codigo de pais', 'pais', 'país', 'code pais', 'country code']),
      region: this.findColumnIndex(headerMap, ['región', 'region', 'state']),
      principal: this.findColumnIndex(headerMap, ['principal', 'is main'])
    };

    return columnMap;
  }

  private findColumnIndex(headerMap: { [key: string]: number }, searchTerms: string[]): number {
    for (const term of searchTerms) {
      if (headerMap[term] !== undefined) {
        return headerMap[term];
      }
    }
    return -1;
  }

  private findCountryByCode(code: string): Country | undefined {
    if (!code || typeof code !== 'string') {
      return undefined;
    }

    const normalizedCode = code.trim().toUpperCase();

    const found = this.countryOptions.find(c => {
      return !!c.iso2 && c.iso2.toUpperCase() === normalizedCode;
    });

    return found;
  }

  private parseBoolean(value: any): boolean {
    if (typeof value === 'boolean') return value;
    if (typeof value === 'string') {
      const normalized = value.toLowerCase().trim();
      return normalized === 'verdadero' || normalized === 'true' || normalized === 'si' || normalized === 'yes' || normalized === '✓' || normalized === '✔';
    }
    return false;
  }

  private getUnprocessedCountries(errors: OfficeBulkImportError[]): string {
    const codes = errors
      .filter(e => e.field === 'pais')
      .map(e => {
        const match = e.message.match(/"([A-Za-z]{2,})"/);
        return match ? match[1].toUpperCase() : null;
      })
      .filter((code): code is string => !!code);

    const uniqueCodes = Array.from(new Set(codes));
    return uniqueCodes.length > 0 ? uniqueCodes.join(', ') : 'códigos de país inválidos';
  }

  getBusinessGroupList() {
    this.isLoading = true;

    this.businessApi.getBusinessGrouped().subscribe({
      next: (response: DataEndpoint<BusinessGroupedModel[]>) => {
        if (response.Status === 200) {
          this.businessGroupOptions = response.Data ?? [];
          this.isLoading = false;
        }
      },
      error: (error: HttpErrorResponse) => {
        this.isLoading = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: error?.error.Message });
      }
    })
  }

  getCountryList() {
    this.countryService.getCountries().subscribe(countries => {
      this.countryOptions = countries;
    });
  }

  get officesFormArray(): FormArray {
    return this.form.get('offices') as FormArray;
  }

  createOfficeFormGroup(): FormGroup {
    return new FormGroup({
      name: new FormControl('', [Validators.required]),
      address: new FormControl('', [Validators.required]),
      country: new FormControl('', [Validators.required]),
      state: new FormControl(''),
      isMain: new FormControl(false),
      city: new FormControl('')
    });
  }

  addOfficeForm() {
    const officeForm = this.createOfficeFormGroup();
    this.officesFormArray.push(officeForm);
  }

  removeOfficeForm(index: number) {
    if (this.officesFormArray.length > 1) {
      this.officesFormArray.removeAt(index);
    }
  }

  // Validación del formulario antes de avanzar
  canGoNext(): boolean {
    if (this.activeStep === 1) {
      // Paso 1: Solo validar que el archivo fue procesado sin errores
      return this.fileProcessedSuccessfully;
    } else if (this.activeStep === 2) {
      // Paso 2: Validar que se seleccionó una empresa y las sedes son válidas
      return this.form.valid;
    }

    return false;
  }

  // Avanzar al siguiente paso
  onClickNextStep(): void {
    if (!this.canGoNext()) {
      this.form.markAllAsTouched();
      return;
    }

    if (this.activeStep < 3) {
      if (this.activeStep === 2) {
        // Solo guardar cuando pasamos de paso 2 a 3
        this.onSaveForm();
      } else {
        // Para otros pasos, solo avanzar
        this.activeStep++;
      }
    }
  }

  // Retroceder al paso anterior
  onClickPreviousStep(): void {
    if (this.activeStep > 1) {
      this.activeStep--;
    }
  }

  onClickCancel(): void {
    this.router.navigate([Routes.officesPage]);
  }

  // Helpers para deshabilitar botones
  isFirstStep(): boolean {
    return this.activeStep === 1;
  }

  isLastStep(): boolean {
    return this.activeStep === 3;
  }

  shouldDisableNextButton(): boolean {
    if (this.isLastStep()) {
      return true;
    }

    // En paso 1: validar que se procesó el archivo
    if (this.activeStep === 1) {
      return !this.fileProcessedSuccessfully;
    }
    // En paso 2: validar que el formulario es válido
    if (this.activeStep === 2) {
      return !this.form.valid;
    }

    return false;
  }

  onSaveForm() {
    if (this.form.valid) {
      this.isLoading = true;

      const businessId = this.form.get('businessId')?.value;
      const offices = this.officesFormArray.value as OfficeFormData[];

      const request: OfficeCreateRequestModel[] = offices.map(officeData => ({
        Name: officeData.name,
        Address: officeData.address,
        BusinessId: businessId,
        IsMain: officeData.isMain,
        Country: officeData.country,
        State: officeData.state,
        City: '-'
      }));

      this.createOffices(request);
    } else {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'El formulario es inválido. Por favor, revisar.' });
    }
  }

  private createOffices(request: OfficeCreateRequestModel[]) {
    this.officeApi.createOffice(request).subscribe({
      next: (response: DataEndpoint<any>) => {
        if (response.Success) {
          this.activeStep = 3;
          this.showSuccessDialog();
        }
        this.isLoading = false;
      },
      error: (error: HttpErrorResponse) => {
        this.isLoading = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: error?.error.Message });
      }
    })
  }

  showSuccessDialog() {
    this.dialogTitle = '¡Sedes creadas!';
    this.dialogMessage = 'Las sedes han sido creadas exitosamente.';
    this.visibleSuccessDialog = true;
  }

  onCloseDialog() {
    this.visibleSuccessDialog = false;
    this.router.navigate([Routes.officesPage]);
  }

  isInvalid(controlName: string, formGroupIndex?: number): boolean {
    let control;
    if (formGroupIndex !== undefined) {
      const officeForm = this.officesFormArray.at(formGroupIndex) as FormGroup;
      control = officeForm.get(controlName);
    } else {
      control = this.form.get(controlName);
    }
    return (control?.invalid && (control.touched)) ?? false;
  }

  getErrorMessage(controlName: string, formGroupIndex?: number): string {
    let control;
    if (formGroupIndex !== undefined) {
      const officeForm = this.officesFormArray.at(formGroupIndex) as FormGroup;
      control = officeForm.get(controlName);
    } else {
      control = this.form.get(controlName);
    }

    if (control?.hasError('required')) {
      return 'El campo es obligatorio.';
    }
    return '';
  }

  getBusinessOptions() {
    return this.businessGroupOptions.flatMap(group =>
      group.Business?.map(business => ({
        label: business.Name,
        value: business.Id,
        logoUrl: business.LogoUrl
      })) || []
    );
  }

  // Getters para FormControls del FormArray
  getOfficeNameControl(index: number): FormControl {
    return this.officesFormArray.at(index).get('name') as FormControl;
  }

  getOfficeAddressControl(index: number): FormControl {
    return this.officesFormArray.at(index).get('address') as FormControl;
  }

  getOfficeCountryControl(index: number): FormControl {
    return this.officesFormArray.at(index).get('country') as FormControl;
  }

  getOfficeStateControl(index: number): FormControl {
    return this.officesFormArray.at(index).get('state') as FormControl;
  }

  getOfficeIsMainControl(index: number): FormControl {
    return this.officesFormArray.at(index).get('isMain') as FormControl;
  }

  getOfficeName(index: number): string {
    const officeName = this.officesFormArray.at(index).get('name')?.value || '(Sin nombre)';
    return `${index + 1} - ${officeName}`;
  }
}
